import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  cancelDeletion,
  formatDate,
  getRegister,
  getSubmission,
  HOUSES,
  reinstate,
  requestDeletion,
  suspend,
  updateMember,
  type MemberStatus,
  type RegisterRow,
} from '../lib/api'
import { Card, Empty, ErrorBox, Spinner, StatusPill, WarnBox } from '../components/bits'
import { Modal, Pager } from '../components/Modal'
import MemberDetail from '../components/MemberDetail'

const PER_PAGE = 25

const STATUSES: { label: string; value: MemberStatus | null }[] = [
  { label: 'All', value: null },
  { label: 'Approved', value: 'approved' },
  { label: 'Pending', value: 'pending' },
  { label: 'Flagged', value: 'flagged' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'On hold', value: 'suspended' },
]

/**
 * Member register — the whole register, including what no member can see.
 *
 * The distinction from the directory is the point of this screen: `admin_members`
 * returns pending, flagged, rejected and private records, because the Secretariat
 * maintains records their owners have chosen to hide.
 *
 * Editing is deliberately narrow. `admin_update_member` enforces a column
 * allow-list server-side, so `status`, `visibility`, `hidden_fields` and `kit_no`
 * are refused with 42501: a verdict belongs to the decision functions, privacy
 * belongs to the member, and a KitNo change is an identity change that has to go
 * back through verification.
 */
export default function MemberRegister() {
  const [rows, setRows] = useState<RegisterRow[]>([])
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [status, setStatus] = useState<MemberStatus | null>(null)
  const [house, setHouse] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editing, setEditing] = useState<RegisterRow | null>(null)
  // Reading a record is not correcting one, so it gets its own state and its
  // own read-only modal. The register list carries twenty columns; the record
  // behind it carries the lot.
  const [viewing, setViewing] = useState<RegisterRow | null>(null)
  // A held or removed member has to be told why, so both actions collect a
  // reason before they will run. One piece of state, two uses.
  const [asking, setAsking] = useState<
    { row: RegisterRow; kind: 'suspend' | 'delete' } | null
  >(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1)
  }, [debounced, status, house])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await getRegister({
        q: debounced,
        status,
        house,
        page,
        perPage: PER_PAGE,
      })
      setRows(r ?? [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [debounced, status, house, page])

  useEffect(() => {
    void load()
  }, [load])

  /** Reinstate and cancel-deletion need no reason, so they run straight away. */
  const runDirect = async (
    row: RegisterRow,
    action: 'reinstate' | 'cancelDeletion',
  ) => {
    setBusyId(row.id)
    setError(null)
    try {
      if (action === 'reinstate') {
        await reinstate(row.id)
        setNotice(`${row.official_name} has been reinstated and can see the directory again.`)
      } else {
        await cancelDeletion(row.id)
        setNotice(`The deletion request for ${row.official_name} has been cancelled.`)
      }
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  const total = rows[0]?.total ?? 0
  const pendingDeletion = rows.filter((r) => r.deletion_requested_at !== null)

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Register</div>
        <h1 style={{ marginTop: 8 }}>Member register</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Every record on the register, whatever its state. Includes pending,
          flagged and private records that do not appear in the member directory.
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {notice && (
        <Card style={{ borderColor: '#2f8fd4' }}>
          <div className="row">
            <span className="small">{notice}</span>
            <div className="spacer" />
            <button className="btn btn-secondary btn-sm" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {pendingDeletion.length > 0 && (
        <WarnBox>
          {pendingDeletion.length}{' '}
          {pendingDeletion.length === 1 ? 'record has' : 'records have'} requested
          deletion and will be removed 30 days after the request. Their audit
          entries are anonymised rather than deleted.
        </WarnBox>
      )}

      <Card>
        <div className="filter-row">
          <input
            className="input"
            style={{ maxWidth: 320 }}
            placeholder="Name, KitNo, email, organisation or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the register"
          />
          {STATUSES.map((s) => (
            <button
              key={s.label}
              className={`tab ${status === s.value ? 'active' : ''}`}
              onClick={() => setStatus(s.value)}
            >
              {s.label}
            </button>
          ))}
          <select
            className="input"
            style={{ maxWidth: 200 }}
            value={house ?? ''}
            onChange={(e) => setHouse(e.target.value === '' ? null : e.target.value)}
            aria-label="Filter by house"
          >
            <option value="">All houses</option>
            {HOUSES.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card style={{ padding: 'var(--s-5)' }}>
        {loading ? (
          <Spinner label="Loading the register…" />
        ) : rows.length === 0 ? (
          <Empty
            title={
              debounced || status || house
                ? 'Nothing matches those filters'
                : 'The register is empty'
            }
            body={
              debounced || status || house
                ? 'Try a different search, status or house.'
                : 'Records appear here as Petarians register, whether or not they have been verified yet.'
            }
          />
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Petarian</th>
                  <th>House</th>
                  <th>Passed</th>
                  <th>Now</th>
                  <th>Privacy</th>
                  <th>Complete</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong style={{ color: 'var(--text)' }}>{r.official_name}</strong>
                      <br />
                      <span className="small muted">
                        KitNo {r.kit_no} · {r.email}
                      </span>
                      {r.suspension_reason && (
                        <>
                          <br />
                          <span className="small" style={{ color: 'var(--suspended)' }}>
                            On hold: {r.suspension_reason}
                          </span>
                        </>
                      )}
                      {r.deletion_requested_at && (
                        <>
                          <br />
                          <span className="small" style={{ color: 'var(--rejected)' }}>
                            Deletion requested {formatDate(r.deletion_requested_at)}
                            {/* Who asked matters: the member themselves, or the
                                Secretariat on their behalf. */}
                            {r.deletion_requested_by
                              ? ` by the Secretariat — ${r.deletion_reason ?? 'no reason recorded'}`
                              : ' by the member'}
                          </span>
                        </>
                      )}
                    </td>
                    <td>{r.house}</td>
                    {/* Since 0037 a null is only ever a record written before
                        the serving-cadet path was removed, never a new one.
                        admin_members does not return still_at_college, so the
                        dash stays deliberately non-committal. */}
                    <td>{r.passing_year ?? '—'}</td>
                    <td className="small">
                      {r.current_position ?? <span className="muted">—</span>}
                      {r.current_organization && (
                        <>
                          <br />
                          <span className="muted">{r.current_organization}</span>
                        </>
                      )}
                    </td>
                    <td className="small">
                      {r.visibility === 'private'
                        ? 'Private'
                        : r.visibility === 'public'
                          ? 'Public'
                          : 'Petarians'}
                      {r.hidden_field_count > 0 && (
                        <>
                          <br />
                          <span className="muted">
                            {r.hidden_field_count} field
                            {r.hidden_field_count === 1 ? '' : 's'} hidden
                          </span>
                        </>
                      )}
                    </td>
                    <td>{r.completeness}%</td>
                    <td>
                      <StatusPill status={r.status} />
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setViewing(r)}
                      >
                        View
                      </button>{' '}
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setEditing(r)}
                        disabled={busyId === r.id}
                      >
                        Correct
                      </button>{' '}
                      {r.status === 'approved' && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setAsking({ row: r, kind: 'suspend' })}
                          disabled={busyId === r.id}
                        >
                          Hold
                        </button>
                      )}
                      {r.status === 'suspended' && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => void runDirect(r, 'reinstate')}
                          disabled={busyId === r.id}
                        >
                          Reinstate
                        </button>
                      )}{' '}
                      {r.deletion_requested_at ? (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => void runDirect(r, 'cancelDeletion')}
                          disabled={busyId === r.id}
                        >
                          Keep
                        </button>
                      ) : (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setAsking({ row: r, kind: 'delete' })}
                          disabled={busyId === r.id}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pager
              page={page}
              perPage={PER_PAGE}
              total={total}
              busy={loading}
              onChange={setPage}
            />
          </>
        )}
      </Card>

      {asking && (
        <ReasonModal
          row={asking.row}
          kind={asking.kind}
          onCancel={() => setAsking(null)}
          onDone={(message) => {
            setAsking(null)
            setNotice(message)
            void load()
          }}
        />
      )}

      {viewing && (
        <RecordModal row={viewing} onClose={() => setViewing(null)} />
      )}

      {editing && (
        <CorrectionModal
          row={editing}
          onCancel={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null)
            setNotice(message)
            void load()
          }}
        />
      )}
    </div>
  )
}

/**
 * The whole record, read-only.
 *
 * The list RPC returns twenty columns; a Petarian's record is the eight
 * repeatable tables plus everything on `members`, and until 0051 none of that
 * reached the portal at all. This fetches the full serialised record — the
 * Secretariat tier of `member_profile`, which is unredacted by design, because
 * you cannot check a submission against the file record if the member's own
 * privacy settings hide half of it.
 */
function RecordModal({
  row,
  onClose,
}: {
  row: RegisterRow
  onClose: () => void
}) {
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)
    getSubmission(row.kit_no)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [row.kit_no])

  return (
    <Modal
      title={`${row.official_name} — KitNo ${row.kit_no}`}
      onClose={onClose}
      wide
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {loading ? (
        <Spinner label="Loading the full record…" />
      ) : failed ? (
        <ErrorBox>
          Could not load this record. Your session may have expired — sign in
          again and reopen it.
        </ErrorBox>
      ) : (
        <MemberDetail detail={detail} />
      )}
    </Modal>
  )
}

/**
 * Correcting a record against the file record.
 *
 * Only the fields the server allows are offered. Anything omitted from the patch
 * is left untouched, so an empty box means "no change" rather than "clear this" —
 * which is why the note explains it: a reviewer must not have to guess.
 */
function CorrectionModal({
  row,
  onCancel,
  onSaved,
}: {
  row: RegisterRow
  onCancel: () => void
  onSaved: (message: string) => void
}) {
  const [name, setName] = useState(row.official_name)
  const [house, setHouse] = useState(row.house)
  const [organisation, setOrganisation] = useState(row.current_organization ?? '')
  const [position, setPosition] = useState(row.current_position ?? '')
  const [city, setCity] = useState(row.city ?? '')
  const [country, setCountry] = useState(row.country ?? '')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /** Only changed fields are sent, so an untouched record produces no write. */
  function buildPatch(): Record<string, string | null> {
    const patch: Record<string, string | null> = {}
    const put = (key: string, next: string, prev: string | null): void => {
      const cleaned = next.trim() === '' ? null : next.trim()
      const before = prev === null || prev.trim() === '' ? null : prev.trim()
      if (cleaned !== before) patch[key] = cleaned
    }
    put('official_name', name, row.official_name)
    if (house !== row.house) patch.house = house
    put('current_organization', organisation, row.current_organization)
    put('current_position', position, row.current_position)
    put('city', city, row.city)
    put('country', country, row.country)
    return patch
  }

  async function save(): Promise<void> {
    if (name.trim().length < 3) {
      setError('The official name cannot be blank.')
      return
    }
    if (note.trim().length < 5) {
      setError('Please note why you are correcting this — it goes in the audit log.')
      return
    }

    const patch = buildPatch()
    if (Object.keys(patch).length === 0) {
      setError('Nothing has changed.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await updateMember(row.id, patch, note.trim())
      onSaved(`${result.officialName} (KitNo ${row.kit_no}) updated.`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`Correct KitNo ${row.kit_no}`}
      onClose={onCancel}
      wide
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save correction'}
          </button>
        </>
      }
    >
      <p className="small" style={{ marginTop: 0 }}>
        For fixing a record against the CCP file record. Verification status,
        privacy settings and KitNo cannot be changed here — a verdict belongs to
        the verification queue, privacy belongs to the member, and a KitNo change
        has to go back through verification.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--s-4)',
          marginTop: 'var(--s-5)',
        }}
      >
        <Field label="Official name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="House">
          <select
            className="input"
            value={house}
            onChange={(e) => setHouse(e.target.value)}
          >
            {HOUSES.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Organisation">
          <input
            className="input"
            value={organisation}
            onChange={(e) => setOrganisation(e.target.value)}
          />
        </Field>
        <Field label="Position">
          <input
            className="input"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
          />
        </Field>
        <Field label="City">
          <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
        </Field>
        <Field label="Country">
          <input
            className="input"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </Field>
      </div>

      <div style={{ marginTop: 'var(--s-5)' }}>
        <Field label="Why (recorded in the audit log)">
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Name spelling corrected from the file record"
          />
        </Field>
      </div>

      {error && (
        <div className="error-box" role="alert" style={{ marginTop: 'var(--s-4)' }}>
          {error}
        </div>
      )}
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="kpi-label">{label}</span>
      <div style={{ marginTop: 6 }}>{children}</div>
    </label>
  )
}


/**
 * Collects the reason for a hold or a removal.
 *
 * Both are refused server-side without one, so this is not the only guard —
 * but a member who is locked out or removed and never told why is the failure
 * this is really preventing, and the server cannot invent the sentence.
 */
function ReasonModal({
  row,
  kind,
  onCancel,
  onDone,
}: {
  row: RegisterRow
  kind: 'suspend' | 'delete'
  onCancel: () => void
  onDone: (message: string) => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hold = kind === 'suspend'

  const go = async () => {
    if (reason.trim() === '') {
      setError('A reason is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (hold) {
        await suspend(row.id, reason.trim())
        onDone(
          `${row.official_name} is on hold and can no longer see the directory. They have been told why.`,
        )
      } else {
        const r = await requestDeletion(row.id, reason.trim())
        onDone(
          `${row.official_name} will be removed on ${formatDate(r.purgesAfter)}. Until then this can be undone with "Keep".`,
        )
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={hold ? `Put ${row.official_name} on hold` : `Remove ${row.official_name}`}
      onClose={onCancel}
    >
      <div className="stack">
        <p className="small">
          {hold ? (
            <>
              Their record stays exactly as it is, but the directory closes to
              them and they disappear from everyone else’s. Reversible at any
              time with Reinstate.
            </>
          ) : (
            <>
              This starts a <strong>thirty-day</strong> countdown, the same one a
              member starts when they delete their own account. Nothing is
              removed today, and “Keep” undoes it right up until the last day.
              After that the record is deleted and its audit entries are
              anonymised rather than kept.
            </>
          )}
        </p>

        <label className="stack" style={{ gap: 6 }}>
          <span className="small muted">
            {hold
              ? 'Reason — the member is sent this'
              : 'Reason — recorded against the request'}
          </span>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              hold
                ? 'e.g. Harvesting contact details from the directory.'
                : 'e.g. Removal requested by telephone on 6 Aug.'
            }
          />
        </label>

        {error && <ErrorBox>{error}</ErrorBox>}

        <div className="row">
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={() => void go()} disabled={busy}>
            {busy ? 'Working…' : hold ? 'Put on hold' : 'Start the 30 days'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
